import { memo, useEffect, useState, type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApiError, ProblemDetailsSchema } from '@/js/libs/api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Loader } from '@/js/libs/ui';
import { useAuth } from '@/js/libs/use-auth';
import { parseError } from '@/js/libs/use-request';

import { useAppLogout } from 'src/hooks/useAppLogout/index.ts';
import { IdentityErrorPage } from './components/index.ts';
import { Impersonation } from './containers/Impersonation.tsx';
import { useGetIdentity } from './hooks/index.ts';

// Loads the logged user and renders under it the impersonation of the selected company or user, behind a loader until
// the user is loaded.
export const Authorization = memo(function Authorization({ children }: PropsWithChildren) {
  const [t] = useTranslation(['common']);
  const { loggedUser, setIdentity, setLoggedUser } = useAuth();
  const logout = useAppLogout();
  const getIdentity = useGetIdentity();
  const [loadError, setLoadError] = useState<ApiError<typeof ProblemDetailsSchema>>();
  // Numbers the loads of the logged user: a reload after a failed load starts the next one.
  const [loadAttempt, setLoadAttempt] = useState(0);

  // Loads the logged user again after a failed load, behind the loader.
  const handleReloadClick = useCallbackRef(() => {
    setLoadError(undefined);
    setLoadAttempt((attempt) => attempt + 1);
  });

  useEffect(() => {
    // Loads the logged user on mount and on every reload. A failed load puts the app behind a page that offers to reload
    // the user or to log in again; a 401 never shows that page, since the interceptors end the session. A load that ends
    // after the unmount discards its outcome.
    let isMounted = true;
    getIdentity().then(
      (loadedUser) => {
        if (isMounted) {
          setLoggedUser(loadedUser);
          setIdentity(loadedUser);
        }
      },
      (error: unknown) => {
        if (isMounted) {
          setLoadError(parseError(error, 'getCurrentUser'));
        }
      },
    );

    return () => {
      isMounted = false;
    };
  }, [getIdentity, loadAttempt, setIdentity, setLoggedUser]);

  if (loadError) {
    return (
      <IdentityErrorPage error={loadError} exitLabel={t('common:BACK_TO_LOGIN_BUTTON')} onExit={logout} onReload={handleReloadClick} />
    );
  }

  if (!loggedUser) {
    return <Loader />;
  }

  return <Impersonation>{children}</Impersonation>;
});
